// pages/MarketSell.jsx
// DWI Market Phase 2 — "Sell to DWI" (the supply side). A seller applies with
// basic details, DWI vets them by hand, and they list what they have: product,
// quantity, their price, where it is, when it's ready, photos. Sellers only
// ever deal with DWI — buyers never see them or their listings. Photos are
// private (SupplyPhoto loads them with the login token).
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { marketAPI } from '../services/api';
import { Button, Card, Input, Select, Badge, Modal, Empty, Spinner } from '../components/ui';
import { SellerDwiUpdates } from './MarketListingEdits';
import { sizeWarnings, groupUnits, SIZE_HINT } from './sizeHints';

const ugx = n => `UGX ${Number(n || 0).toLocaleString('en-US')}`;
const day = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const dateInput = d => (d ? new Date(d).toISOString().slice(0, 10) : '');

export const LISTING_STATUS = {
  submitted: ['Waiting for DWI to review', 'amber'],
  approved:  ['Approved — DWI will contact you', 'cyan'],
  published: ['Offered to buyers by DWI', 'green'],
  rejected:  ['Not approved', 'red'],
  sold_out:  ['Sold out', 'gray'],
  withdrawn: ['Withdrawn', 'gray'],
};
const SELLER_STATUS = {
  pending:   ['Application under review', 'amber'],
  approved:  ['Approved seller', 'green'],
  rejected:  ['Application not approved', 'red'],
  suspended: ['Suspended', 'red'],
};

// Private seller photo, fetched with the login token (never a public link).
export function SupplyPhoto({ name, className = 'w-20 h-20', onClick }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true; let made = null;
    marketAPI.supplyPhoto(name)
      .then(blob => { if (!live) return; made = URL.createObjectURL(blob); setUrl(made); })
      .catch(() => live && setFailed(true));
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [name]);
  const box = `${className} rounded-lg border border-border object-cover`;
  if (failed) return <div className={`${box} bg-bg-elevated flex items-center justify-center text-[10px] text-text-muted`}>no photo</div>;
  if (!url) return <div className={`${box} bg-bg-elevated flex items-center justify-center`}><Spinner size="sm" /></div>;
  return <img src={url} alt="" className={`${box} ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick ? () => onClick(url) : undefined} />;
}

export default function SellTab({ contact }) {
  const [data, setData]       = useState(null);
  const [closed, setClosed]   = useState(false);
  const [editing, setEditing] = useState(null);   // listing, or {} for new
  const [editApp, setEditApp] = useState(false);

  const load = useCallback(async () => {
    try { setData(await marketAPI.sellMe()); }
    catch (err) { if (err.status === 503) setClosed(true); else toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (closed) {
    return (
      <Card>
        <Empty icon="🌾" title="Selling to DWI opens soon"
          description="Farmers, fishers, traders and suppliers will be able to list their goods here. Until then, contact DwiSend directly." />
        <DwiContact contact={contact} text="Hello DwiSend, I would like to sell to DWI. I have: " />
      </Card>
    );
  }
  if (!data) return <div className="py-16 flex justify-center"><Spinner /></div>;

  const { seller, listings, options } = data;
  if (!seller || editApp) {
    return <Application seller={seller} onCancel={seller ? () => setEditApp(false) : null}
      onSaved={() => { setEditApp(false); load(); }} />;
  }
  const [label, color] = SELLER_STATUS[seller.status];
  const canList = ['pending', 'approved'].includes(seller.status);

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-text-primary font-bold">{seller.businessName}</div>
            <div className="text-[11px] text-text-muted">{seller.district}{seller.location ? ` · ${seller.location}` : ''} · {seller.phone}</div>
            <div className="mt-2"><Badge label={label} color={color} /></div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {seller.status !== 'suspended' && <Button size="sm" variant="secondary" onClick={() => setEditApp(true)}>{seller.status === 'rejected' ? 'Update & apply again' : 'Edit my details'}</Button>}
            {canList && <Button size="sm" onClick={() => setEditing({})}>+ Add goods</Button>}
          </div>
        </div>
        <p className="text-xs text-text-muted mt-3">
          {seller.status === 'pending' && 'DWI checks every seller before buying. You can add your goods now — DWI reviews them once you are approved.'}
          {seller.status === 'approved' && 'Add what you have. DWI reviews each listing and contacts you to agree the deal. Buyers never see your name or number — they buy from DWI.'}
          {seller.status === 'rejected' && <>Reason: <span className="text-text-primary">{seller.statusReason}</span>. Update your details to apply again.</>}
          {seller.status === 'suspended' && <>Reason: <span className="text-text-primary">{seller.statusReason}</span>. Contact DwiSend.</>}
        </p>
        <DwiContact contact={contact} text={`Hello DwiSend, this is ${seller.businessName} (seller). `} />
      </Card>

      {listings.length === 0
        ? <Card><Empty icon="📦" title="No goods listed yet" description={canList ? 'Tap "+ Add goods": what it is, how much, your price, where it is.' : ''} /></Card>
        : listings.map(l => <ListingCard key={l._id} l={l} seller={seller} contact={contact} onEdit={() => setEditing(l)} onChanged={load} />)}

      {editing && <ListingModal listing={editing} options={options} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function DwiContact({ contact, text }) {
  const navigate = useNavigate();
  return (
    <div className="flex gap-2 flex-wrap mt-3">
      {contact?.whatsapp && (
        <a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer">
          <Button size="sm" variant="success">WhatsApp DWI</Button>
        </a>
      )}
      {contact?.phone && <a href={`tel:${contact.phone.replace(/\s/g, '')}`}><Button size="sm" variant="secondary">📞 {contact.phone}</Button></a>}
      <Button size="sm" variant="ghost" onClick={() => navigate('/market?tab=messages&topic=selling')}>💬 Message in the app</Button>
    </div>
  );
}

function Application({ seller, onCancel, onSaved }) {
  const [f, setF] = useState({
    businessName: seller?.businessName || '', phone: seller?.phone || '', whatsapp: seller?.whatsapp || '',
    district: seller?.district || '', location: seller?.location || '', supplies: seller?.supplies || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await marketAPI.sellApply(f); toast.success(seller ? 'Details saved' : 'Application sent — DWI will review it'); onSaved(); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <div className="text-text-primary font-bold">{seller ? 'Your seller details' : 'Sell to DWI'}</div>
      <p className="text-xs text-text-muted mt-1 mb-4">
        Farmers, fishers, traders and suppliers: DWI buys for DwiSend. You deal with DWI only — DWI finds the buyers and
        arranges delivery. DWI checks every seller before buying.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Business or trading name" value={f.businessName} onChange={e => set('businessName', e.target.value)} placeholder="e.g. Okello Farm Supplies (or your own name)" />
        <Input label="Phone" value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="07XXXXXXXX" />
        <Input label="WhatsApp (optional, with country code)" value={f.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="2567XXXXXXXX" />
        <Input label="District" value={f.district} onChange={e => set('district', e.target.value)} placeholder="e.g. Kasese" />
      </div>
      <div className="mt-3 space-y-3">
        <Input label="Location (optional)" value={f.location} onChange={e => set('location', e.target.value)} placeholder="Village, market or landmark" />
        <div>
          <label className="text-xs font-semibold text-text-secondary">What do you sell?</label>
          <textarea rows={3} maxLength={500} value={f.supplies} onChange={e => set('supplies', e.target.value)}
            placeholder="e.g. Beans and maize — about 2 tonnes a month; or fresh tilapia every week"
            className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        {onCancel && <Button variant="secondary" onClick={onCancel}>Cancel</Button>}
        <Button loading={saving} onClick={save}>{seller ? 'Save' : 'Apply to sell'}</Button>
      </div>
    </Card>
  );
}

function ListingCard({ l, seller, contact, onEdit, onChanged }) {
  const navigate = useNavigate();
  const [label, color] = LISTING_STATUS[l.status];
  const [busy, setBusy] = useState(false);
  const canEdit = seller.status !== 'suspended' && seller.status !== 'rejected' && l.status !== 'withdrawn';
  const act = async (action, ok, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try { await marketAPI.sellAction(l._id, action); toast.success(ok); onChanged(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="flex gap-3 flex-col sm:flex-row">
        {l.photos.length > 0 && <div className="flex gap-1 flex-wrap sm:w-44">{l.photos.slice(0, 3).map(p => <SupplyPhoto key={p} name={p} className="w-14 h-14" />)}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="text-text-primary font-bold">{l.productName}</div>
              <div className="text-[11px] text-text-muted">{l.ref} · {l.categoryName}</div>
            </div>
            <Badge label={label} color={color} />
          </div>
          <div className="text-sm mt-2">{l.quantity.toLocaleString()} × {l.unitName} at <span className="text-text-primary font-semibold">{ugx(l.priceUGX)}</span> each</div>
          <div className="text-[11px] text-text-muted">Total {ugx(l.quantity * l.priceUGX)} · {l.location}{l.readyDate ? ` · ready ${day(l.readyDate)}` : ' · ready now'}</div>
          {l.status === 'rejected' && l.statusReason && <div className="text-xs text-red mt-1">Reason: {l.statusReason}</div>}
          <SellerDwiUpdates updates={l.dwiUpdates} onChanged={onChanged} />
          <div className="flex gap-2 flex-wrap mt-3">
            {canEdit && <Button size="xs" variant="secondary" onClick={onEdit}>{['submitted', 'rejected'].includes(l.status) ? 'Edit' : 'Update quantity / price'}</Button>}
            {['approved', 'published'].includes(l.status) && <Button size="xs" variant="ghost" loading={busy} onClick={() => act('sold_out', 'Marked sold out')}>Sold out</Button>}
            {l.status !== 'withdrawn' && <Button size="xs" variant="ghost" loading={busy} onClick={() => act('withdraw', 'Listing withdrawn', `Withdraw ${l.productName}? DWI will stop considering it.`)}>Withdraw</Button>}
            <Button size="xs" variant="secondary" onClick={() => navigate(`/market?tab=messages&topic=selling&listing=${l._id}&subject=${encodeURIComponent(`${l.productName} (${l.ref})`)}`)}>💬 Message DWI</Button>
            {contact?.whatsapp && (
              <a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(`Hello DwiSend, about my listing ${l.ref} (${l.productName}): `)}`} target="_blank" rel="noopener noreferrer">
                <Button size="xs" variant="success">WhatsApp DWI</Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export function ListingModal({ listing, options, onClose, onSaved }) {
  const isNew = !listing._id;
  const locked = !isNew && !['submitted', 'rejected'].includes(listing.status);   // after approval: qty/price/date/notes/photos only
  const [f, setF] = useState(() => ({
    productName: listing.productName || '', categoryId: listing.categoryId || options.categories[0]?._id || '',
    unitId: listing.unitId || '', quantity: listing.quantity ?? '', priceUGX: listing.priceUGX ?? '',
    location: listing.location || '', readyDate: dateInput(listing.readyDate), notes: listing.notes || '', photos: listing.photos || [],
    weightKg: listing.weightGrams == null ? '' : String(listing.weightGrams / 1000),
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const unit = options.units.find(u => u._id === f.unitId);
  const qty = Number(f.quantity); const price = Number(f.priceUGX);
  const { single, packs } = groupUnits(options.units);
  const warnings = sizeWarnings({ productName: f.productName, notes: f.notes, unit });

  const upload = async e => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (f.photos.length + files.length > 6) return toast.error('Up to 6 photos');
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} is over 5 MB`); continue; }
        const r = await marketAPI.sellPhoto(file);
        setF(p => ({ ...p, photos: [...p.photos, r.name] }));
      }
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.unitId) return toast.error('Choose how you sell it (Sold by)');
    if (warnings.length && !window.confirm(`Please check the size:\n\n• ${warnings.join('\n• ')}\n\nSend anyway?`)) return;
    const body = locked
      ? { quantity: qty, priceUGX: price, readyDate: f.readyDate || null, notes: f.notes, photos: f.photos, weightKg: f.weightKg }
      : { ...f, quantity: qty, priceUGX: price, readyDate: f.readyDate || null };
    setSaving(true);
    try {
      if (isNew) await marketAPI.sellCreate(body); else await marketAPI.sellUpdate(listing._id, body);
      toast.success(isNew ? 'Sent to DWI for review' : 'Saved');
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? 'Add goods to sell to DWI' : `Edit ${listing.productName}`} size="lg">
      <div className="space-y-4">
        {locked && <p className="text-xs text-amber">Approved listings: you can change quantity, price, ready date, weight, notes and photos. For anything else, message DWI.</p>}
        <Input label="Product" value={f.productName} disabled={locked} onChange={e => set('productName', e.target.value)} placeholder="e.g. Beans (Nambale), Tilapia, Matooke" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Select label="Category" value={f.categoryId} disabled={locked} onChange={e => set('categoryId', e.target.value)}>
            {options.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          <Select label="Sold by" value={f.unitId} disabled={locked} onChange={e => set('unitId', e.target.value)}>
            <option value="">— Choose how you sell it —</option>
            {single.length > 0 && <optgroup label="One item or loose amount">{single.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}</optgroup>}
            {packs.length > 0 && <optgroup label="Packs and bags">{packs.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}</optgroup>}
          </Select>
          <Input label={`Quantity you have (${unit?.name || 'units'})`} type="number" min="1" step="1" value={f.quantity} onChange={e => set('quantity', e.target.value)} />
          <Input label={`Your price per ${unit?.name || 'unit'} (UGX)`} type="number" min="1" step="1" value={f.priceUGX} onChange={e => set('priceUGX', e.target.value)} />
        </div>
        {!locked && <p className="text-xs text-text-muted -mt-2">{SIZE_HINT}</p>}
        {warnings.length > 0 && (
          <div className="text-xs text-amber border border-amber/40 rounded-lg p-2 space-y-1" data-testid="size-warning">
            {warnings.map(w => <div key={w}>⚠ {w}</div>)}
          </div>
        )}
        {qty > 0 && price > 0 && <div className="text-xs text-text-muted">All of it: {qty.toLocaleString()} × {ugx(price)} = <span className="text-text-primary font-semibold">{ugx(qty * price)}</span></div>}
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Where are the goods?" value={f.location} disabled={locked} onChange={e => set('location', e.target.value)} placeholder="Store, market or farm, district" />
          <Input label="Ready from (blank = now)" type="date" value={f.readyDate} onChange={e => set('readyDate', e.target.value)} />
          <Input label={`Weight of one ${unit?.name || 'unit'} in kg (optional)`} inputMode="decimal" value={f.weightKg} onChange={e => set('weightKg', e.target.value)} placeholder="e.g. 1 or 50" />
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary">Notes (optional)</label>
          <textarea rows={2} maxLength={1000} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Quality, grade, variety, how it's packed…"
            className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
        </div>
        <div>
          <div className="text-xs font-semibold text-text-secondary mb-2">Photos ({f.photos.length}/6) — only DWI sees them</div>
          <div className="flex gap-2 flex-wrap">
            {f.photos.map((name, i) => (
              <div key={name} className="relative">
                <SupplyPhoto name={name} />
                <button className="absolute -top-2 -right-2 bg-red text-white rounded-full w-5 h-5 text-xs" title="Remove"
                  onClick={() => set('photos', f.photos.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            {f.photos.length < 6 && (
              <label className="w-20 h-20 rounded-lg border border-dashed border-border flex items-center justify-center text-xs text-text-muted cursor-pointer hover:border-cyan">
                {uploading ? <Spinner size="sm" /> : '+ Photo'}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} disabled={uploading} />
              </label>
            )}
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={saving} disabled={uploading} onClick={save}>{isNew ? 'Send to DWI' : 'Save'}</Button>
        </div>
      </div>
    </Modal>
  );
}
